// Command palette (⌘⇧P / Ctrl+Shift+P). A fast, fuzzy-filtered launcher over
// every IDE action plus an "Open <file>" entry per workspace file. Parameterless:
// it reads the store, builds the command set, and dispatches store actions. The
// fuzzy matcher is a subsequence match ranked by contiguity, with matched-char
// highlighting, the Zed "command palette" feel: dense rows grouped by section,
// a real lucide glyph per command, and a right-aligned keycap hint.

import { useEffect, useMemo, useRef, useState } from "react";
import type { JSX, ReactNode } from "react";
import {
  Play,
  FilePlus2,
  SunMoon,
  PanelLeft,
  PanelBottom,
  Settings2,
  FileCode2,
  FileJson2,
  FileText,
  Search,
  SearchX,
  CornerDownLeft,
} from "lucide-react";
import { useIde } from "../store";
import { NEW_CONTRACT_TEMPLATE } from "../fs/examples";
import { THEMES } from "../theme";
import "./overlays.css";

/** A single runnable command in the palette. */
interface Command {
  id: string;
  title: string;
  /** Category, used to group rows under a section header. */
  group: string;
  /** Human keybinding hint, right-aligned, split into per-key keycaps. */
  keys?: string[];
  /** Lucide glyph (16px, inherits currentColor). */
  icon: ReactNode;
  run: () => void;
}

/** A scored match: the command plus which title indices matched the query. */
interface Scored {
  cmd: Command;
  score: number;
  /** Indices into cmd.title that the query characters matched. */
  matched: number[];
}

const isMac =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform);
const MOD = isMac ? "⌘" : "Ctrl";

const ICON = { size: 16, strokeWidth: 1.75 } as const;

/** Pick a file glyph by extension, the way an editor's tree would. */
function fileIcon(path: string): ReactNode {
  if (path.endsWith(".sl")) return <FileCode2 {...ICON} />;
  if (path.endsWith(".json")) return <FileJson2 {...ICON} />;
  return <FileText {...ICON} />;
}

/**
 * Fuzzy subsequence match. Returns null when `query` is not a subsequence of
 * `text`; otherwise a score (higher = better) plus the matched indices. Ranking
 * favours: contiguous runs, matches at word starts, and earlier first-match.
 */
function fuzzy(query: string, text: string): { score: number; matched: number[] } | null {
  if (!query) return { score: 0, matched: [] };
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  const matched: number[] = [];
  let qi = 0;
  let score = 0;
  let run = 0;
  let prev = -2;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) {
      matched.push(ti);
      const contiguous = ti === prev + 1;
      run = contiguous ? run + 1 : 1;
      score += run * 4; // reward longer contiguous runs
      const atWordStart = ti === 0 || /[\s/._-]/.test(t[ti - 1]);
      if (atWordStart) score += 8;
      if (ti < 6) score += 2; // early matches read as more relevant
      prev = ti;
      qi++;
    }
  }
  if (qi < q.length) return null; // not all query chars consumed
  score -= (t.length - q.length) * 0.15; // mild preference for tighter targets
  return { score, matched };
}

/** Title with matched characters wrapped in <mark>. */
function Highlight({ text, matched }: { text: string; matched: number[] }): JSX.Element {
  if (matched.length === 0) return <>{text}</>;
  const set = new Set(matched);
  const out: JSX.Element[] = [];
  let buf = "";
  let bufIsMatch = set.has(0);
  const flush = (i: number, asMatch: boolean) => {
    if (!buf) return;
    out.push(
      asMatch ? (
        <mark key={i} className="cmdp-mark">
          {buf}
        </mark>
      ) : (
        <span key={i}>{buf}</span>
      ),
    );
    buf = "";
  };
  for (let i = 0; i < text.length; i++) {
    const isMatch = set.has(i);
    if (isMatch !== bufIsMatch) {
      flush(i, bufIsMatch);
      bufIsMatch = isMatch;
    }
    buf += text[i];
  }
  flush(text.length, bufIsMatch);
  return <>{out}</>;
}

/** Render a keybinding hint as discrete keycaps. */
function Keys({ keys }: { keys: string[] }): JSX.Element {
  return (
    <span className="cmdp-row-keys" aria-hidden="true">
      {keys.map((k, i) => (
        <kbd key={i} className="cmdp-key">
          {k}
        </kbd>
      ))}
    </span>
  );
}

/** Derive a unique "untitled-N.sl" path not already in the workspace. */
function uniqueUntitled(files: Record<string, unknown>): string {
  if (!files["untitled.sl"]) return "untitled.sl";
  for (let n = 1; ; n++) {
    const p = `untitled-${n}.sl`;
    if (!files[p]) return p;
  }
}

export function CommandPalette(): JSX.Element | null {
  const open = useIde((s) => s.commandPaletteOpen);
  const setCommandPalette = useIde((s) => s.setCommandPalette);
  const files = useIde((s) => s.files);
  const theme = useIde((s) => s.prefs.theme);

  const runCompile = useIde((s) => s.runCompile);
  const newFile = useIde((s) => s.newFile);
  const setTheme = useIde((s) => s.setTheme);
  const toggleSidebar = useIde((s) => s.toggleSidebar);
  const toggleBottom = useIde((s) => s.toggleBottom);
  const setActivityView = useIde((s) => s.setActivityView);
  const openFile = useIde((s) => s.openFile);

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const close = () => setCommandPalette(false);

  // Build the command set. Recomputed when the inputs that affect it change.
  const commands = useMemo<Command[]>(() => {
    const base: Command[] = [
      {
        id: "compile",
        group: "Build",
        title: "Compile contract",
        keys: [MOD, "↵"],
        icon: <Play {...ICON} fill="currentColor" />,
        run: () => void runCompile("fund"),
      },
      {
        id: "new",
        group: "File",
        title: "New contract",
        icon: <FilePlus2 {...ICON} />,
        run: () => newFile(uniqueUntitled(files), NEW_CONTRACT_TEMPLATE),
      },
      ...THEMES.map(
        (t): Command => ({
          id: `theme:${t.id}`,
          group: "View",
          title: `Theme: ${t.label}${theme === t.id ? "  (current)" : ""}`,
          icon: <SunMoon {...ICON} />,
          run: () => setTheme(t.id),
        }),
      ),
      {
        id: "sidebar",
        group: "View",
        title: "Toggle sidebar",
        keys: [MOD, "B"],
        icon: <PanelLeft {...ICON} />,
        run: () => toggleSidebar(),
      },
      {
        id: "bottom",
        group: "View",
        title: "Toggle bottom panel",
        keys: [MOD, "J"],
        icon: <PanelBottom {...ICON} />,
        run: () => toggleBottom(),
      },
      {
        id: "settings",
        group: "View",
        title: "Open Settings",
        icon: <Settings2 {...ICON} />,
        run: () => setActivityView("settings"),
      },
    ];
    const fileCmds: Command[] = Object.keys(files)
      .sort((a, b) => a.localeCompare(b))
      .map((path) => ({
        id: `open:${path}`,
        group: "Go to File",
        title: `Open ${path}`,
        icon: fileIcon(path),
        run: () => openFile(path),
      }));
    return [...base, ...fileCmds];
  }, [
    files,
    theme,
    runCompile,
    newFile,
    setTheme,
    toggleSidebar,
    toggleBottom,
    setActivityView,
    openFile,
  ]);

  // Filter + rank.
  const results = useMemo<Scored[]>(() => {
    const q = query.trim();
    const scored: Scored[] = [];
    for (const cmd of commands) {
      const m = fuzzy(q, cmd.title);
      if (m) scored.push({ cmd, score: m.score, matched: m.matched });
    }
    if (!q) return scored; // preserve declared order when unfiltered
    return scored.sort((a, b) => b.score - a.score || a.cmd.title.localeCompare(b.cmd.title));
  }, [commands, query]);

  // Section breaks: only label a group when it differs from the previous row's.
  // When filtering, results are re-ordered by score so a header marks the first
  // row of each *contiguous* run of the same group, exactly like Zed.
  const showGroupAt = useMemo<boolean[]>(() => {
    return results.map((hit, i) => i === 0 || hit.cmd.group !== results[i - 1].cmd.group);
  }, [results]);

  // Reset state each time the palette opens; focus the input.
  useEffect(() => {
    if (open) {
      setQuery("");
      setSelected(0);
      // Focus after paint so autofocus reliably lands.
      const id = window.requestAnimationFrame(() => inputRef.current?.focus());
      return () => window.cancelAnimationFrame(id);
    }
    return undefined;
  }, [open]);

  // Keep selection in range as the result set shrinks/grows.
  useEffect(() => {
    setSelected((s) => (results.length === 0 ? 0 : Math.min(s, results.length - 1)));
  }, [results.length]);

  // Keep the selected row scrolled into view.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector<HTMLElement>(`[data-idx="${selected}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [selected, results.length]);

  if (!open) return null;

  const runAt = (idx: number) => {
    const hit = results[idx];
    if (!hit) return;
    close();
    hit.cmd.run();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelected((s) => (results.length === 0 ? 0 : (s + 1) % results.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelected((s) => (results.length === 0 ? 0 : (s - 1 + results.length) % results.length));
    } else if (e.key === "Enter") {
      e.preventDefault();
      runAt(selected);
    } else if (e.key === "Home") {
      e.preventDefault();
      setSelected(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setSelected(Math.max(0, results.length - 1));
    }
  };

  return (
    <div
      className="cmdp-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        className="cmdp-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="cmdp-search">
          <Search className="cmdp-search-icon" size={16} strokeWidth={1.75} aria-hidden="true" />
          <input
            ref={inputRef}
            className="cmdp-input"
            type="text"
            placeholder="Type a command or file name…"
            spellCheck={false}
            autoComplete="off"
            value={query}
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdp-listbox"
            aria-activedescendant={results[selected] ? `cmdp-opt-${selected}` : undefined}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelected(0);
            }}
            onKeyDown={onKeyDown}
          />
          {results.length > 0 && (
            <span className="cmdp-count" aria-hidden="true">
              {results.length}
            </span>
          )}
        </div>

        <div className="cmdp-results" ref={listRef} role="listbox" id="cmdp-listbox">
          {results.length === 0 ? (
            <div className="cmdp-empty">
              <SearchX size={22} strokeWidth={1.5} className="cmdp-empty-icon" aria-hidden="true" />
              <span className="cmdp-empty-title">No matching commands</span>
              <span className="cmdp-empty-sub">
                Nothing matches <span className="cmdp-empty-q">{query.trim()}</span>. Check the
                spelling, or try a shorter search.
              </span>
            </div>
          ) : (
            results.map((hit, idx) => (
              <div key={hit.cmd.id} className="cmdp-cell">
                {showGroupAt[idx] && (
                  <div className="cmdp-group-head" aria-hidden="true">
                    {hit.cmd.group}
                  </div>
                )}
                <div
                  id={`cmdp-opt-${idx}`}
                  data-idx={idx}
                  role="option"
                  aria-selected={idx === selected}
                  className={`cmdp-row${idx === selected ? " is-selected" : ""}`}
                  onMouseMove={() => setSelected(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault(); // keep focus in the input
                    runAt(idx);
                  }}
                >
                  <span className="cmdp-row-icon">{hit.cmd.icon}</span>
                  <span className="cmdp-row-title">
                    <Highlight text={hit.cmd.title} matched={hit.matched} />
                  </span>
                  {idx === selected ? (
                    hit.cmd.keys ? (
                      <Keys keys={hit.cmd.keys} />
                    ) : (
                      <span className="cmdp-row-enter" aria-hidden="true">
                        <CornerDownLeft size={13} strokeWidth={1.75} />
                      </span>
                    )
                  ) : (
                    hit.cmd.keys && <Keys keys={hit.cmd.keys} />
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="cmdp-footer" aria-hidden="true">
          <span className="cmdp-foot-grp">
            <kbd className="cmdp-key">↑</kbd>
            <kbd className="cmdp-key">↓</kbd>
            <span>Navigate</span>
          </span>
          <span className="cmdp-foot-grp">
            <kbd className="cmdp-key">↵</kbd>
            <span>Run</span>
          </span>
          <span className="cmdp-foot-grp">
            <kbd className="cmdp-key">esc</kbd>
            <span>Dismiss</span>
          </span>
        </div>
      </div>
    </div>
  );
}
